import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, X, CalendarX, Search } from 'lucide-react';
import { planningApi } from './api';
import { PlanningResource, PlanningResourceType, PlanningResourceCalendarException, PlanningResourceOption, RESOURCE_TYPE_LABEL, CALENDAR_EXCEPTION_LABEL, PlanningCalendarExceptionType } from './types';

const EMPTY_FORM: Partial<PlanningResource> = { type: 'PERSONA', sourceId: undefined, name: '', profile: '', dailyHours: 8, workingDays: '1,2,3,4,5', active: 1 };
const ALL_TYPES: PlanningResourceType[] = ['PERSONA', 'VEHICLE', 'MAQUINA', 'EXTERN'];

export default function ResourcesTab() {
  const [resources, setResources] = useState<PlanningResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<PlanningResource>>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [selectedResource, setSelectedResource] = useState<PlanningResource | null>(null);
  const [exceptions, setExceptions] = useState<PlanningResourceCalendarException[]>([]);
  const [excForm, setExcForm] = useState<Partial<PlanningResourceCalendarException>>({ type: 'VACANCES', startDate: '', endDate: '' });
  const [sourceOptions, setSourceOptions] = useState<(PlanningResourceOption & { type: PlanningResourceType })[]>([]);
  const [optionSearch, setOptionSearch] = useState('');
  const [optionTypeFilter, setOptionTypeFilter] = useState<PlanningResourceType | ''>('');
  const [optionGrup, setOptionGrup] = useState('');

  const load = () => {
    setLoading(true);
    planningApi.listResources(filterType || undefined, 'all').then(setResources).finally(() => setLoading(false));
  };
  useEffect(load, [filterType]);

  // Carrega totes les opcions (empleats, equips, proveïdors) dels 4 tipus
  // alhora, perquè l'usuari pugui buscar/filtrar directament sense haver de
  // triar primer un tipus (p.ex. no sabia si un equip és "Vehicle" o
  // "Maquina" segons com l'han classificat a Infraestructures).
  useEffect(() => {
    if (!showForm) return;
    setOptionSearch('');
    setOptionTypeFilter('');
    setOptionGrup('');
    Promise.all(
      ALL_TYPES.map((t) => planningApi.listResourceOptions(t, { resourceId: editingId ?? undefined }).then((list) => list.map((o) => ({ ...o, type: t }))))
    ).then((results) => setSourceOptions(results.flat()));
  }, [showForm, editingId]);

  const startCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const startEdit = (r: PlanningResource) => { setEditingId(r.id); setForm(r); setShowForm(true); };

  const pickSource = (o: PlanningResourceOption & { type: PlanningResourceType }) => {
    setForm((f) => ({ ...f, sourceId: o.id, type: o.type, name: o.label, profile: o.sublabel }));
  };

  const save = async () => {
    if (!editingId && !form.sourceId) { alert('Selecciona un empleat, equip o proveïdor de la llista'); return; }
    if (!form.name?.trim()) { alert('El nom és obligatori'); return; }
    if (editingId) await planningApi.updateResource(editingId, form);
    else await planningApi.createResource(form);
    setShowForm(false);
    load();
  };

  const grupOptions = Array.from(new Set(sourceOptions.filter((o) => o.type === 'VEHICLE' || o.type === 'MAQUINA').map((o) => o.grup).filter((g): g is string => !!g))).sort();
  const filteredOptions = sourceOptions.filter((o) =>
    (!optionTypeFilter || o.type === optionTypeFilter) &&
    (!optionGrup || o.grup === optionGrup) &&
    (!optionSearch || o.label.toLowerCase().includes(optionSearch.toLowerCase()) || (o.sublabel ?? '').toLowerCase().includes(optionSearch.toLowerCase()))
  );

  const remove = async (r: PlanningResource) => {
    if (!confirm(`Eliminar el recurs "${r.name}"?`)) return;
    await planningApi.removeResource(r.id);
    if (selectedResource?.id === r.id) setSelectedResource(null);
    load();
  };

  const openResource = async (r: PlanningResource) => {
    setSelectedResource(r);
    const data = await planningApi.listCalendarExceptions(r.id);
    setExceptions(data);
  };

  const addException = async () => {
    if (!selectedResource || !excForm.startDate || !excForm.endDate) return;
    await planningApi.addCalendarException(selectedResource.id, excForm);
    setExcForm({ type: 'VACANCES', startDate: '', endDate: '' });
    setExceptions(await planningApi.listCalendarExceptions(selectedResource.id));
  };

  const removeException = async (id: string) => {
    if (!selectedResource) return;
    await planningApi.removeCalendarException(selectedResource.id, id);
    setExceptions(await planningApi.listCalendarExceptions(selectedResource.id));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Recursos</h3>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="text-xs border border-gray-200 rounded px-2 py-1">
              <option value="">Tots els tipus</option>
              {Object.entries(RESOURCE_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button onClick={startCreate} className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">
            <Plus size={14} /> Nou recurs
          </button>
        </div>

        {showForm && (
          <div className="p-4 border-b border-gray-100 bg-gray-50 grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Empleat, equip o proveïdor</label>
              {form.sourceId ? (
                <div className="flex items-center justify-between px-2 py-1.5 border border-blue-200 bg-blue-50 rounded text-sm">
                  <div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/70 text-gray-600 mr-1.5">{form.type ? RESOURCE_TYPE_LABEL[form.type as PlanningResourceType] : ''}</span>
                    <span className="font-medium text-gray-900">{form.name}</span>
                    {form.profile && <span className="text-muted-foreground"> · {form.profile}</span>}
                  </div>
                  <button onClick={() => setForm((f) => ({ ...f, sourceId: undefined, name: '', profile: '' }))} className="text-xs text-blue-700 hover:underline shrink-0 ml-2">Canviar</button>
                </div>
              ) : (
                <div className="border border-gray-200 rounded bg-white">
                  <div className="flex items-center gap-2 p-2 border-b border-gray-100">
                    <div className="flex-1 flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded">
                      <Search size={12} className="text-muted-foreground" />
                      <input value={optionSearch} onChange={(e) => setOptionSearch(e.target.value)} placeholder="Cercar per nom..." className="w-full text-xs outline-none" />
                    </div>
                    <select value={optionTypeFilter} onChange={(e) => { setOptionTypeFilter(e.target.value as PlanningResourceType | ''); setOptionGrup(''); }} className="text-xs border border-gray-200 rounded px-1.5 py-1">
                      <option value="">Tots els tipus</option>
                      {Object.entries(RESOURCE_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {(optionTypeFilter === 'VEHICLE' || optionTypeFilter === 'MAQUINA' || !optionTypeFilter) && grupOptions.length > 0 && (
                      <select value={optionGrup} onChange={(e) => setOptionGrup(e.target.value)} className="text-xs border border-gray-200 rounded px-1.5 py-1">
                        <option value="">Tots els grups</option>
                        {grupOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                    {filteredOptions.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground p-2">Cap element disponible (o ja estan tots vinculats)</p>
                    ) : filteredOptions.map((o) => (
                      <button key={`${o.type}-${o.id}`} onClick={() => pickSource(o)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 flex items-center gap-1.5">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">{RESOURCE_TYPE_LABEL[o.type]}</span>
                        <span className="font-medium text-gray-900">{o.label}</span>
                        {o.sublabel && <span className="text-muted-foreground"> · {o.sublabel}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!form.sourceId && editingId && (
                <p className="text-[10px] text-muted-foreground mt-1">Recurs antic sense vincle. Pots deixar-lo així o seleccionar un element de la llista per vincular-lo ara.</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Hores/dia (jornada base)</label>
              <input type="number" step="0.5" value={form.dailyHours ?? 8} onChange={(e) => setForm((f) => ({ ...f, dailyHours: Number(e.target.value) }))} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Dies laborables (1=dilluns..7=diumenge, separats per coma)</label>
              <input value={form.workingDays ?? '1,2,3,4,5'} onChange={(e) => setForm((f) => ({ ...f, workingDays: e.target.value }))} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.active !== 0} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))} />
              Actiu
            </label>
            <div className="col-span-2 flex items-center gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-100">Cancel·lar</button>
              <button onClick={save} className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700">Desar</button>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Carregant...</div>
          ) : resources.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Sense recursos</div>
          ) : resources.map((r) => (
            <button key={r.id} onClick={() => openResource(r)} className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 ${selectedResource?.id === r.id ? 'bg-blue-50' : ''}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900 truncate">{r.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{RESOURCE_TYPE_LABEL[r.type]}</span>
                  {!r.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600">Inactiu</span>}
                </div>
                {r.profile && <p className="text-[11px] text-muted-foreground truncate">{r.profile}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span onClick={(e) => { e.stopPropagation(); startEdit(r); }} className="p-1 hover:bg-gray-200 rounded"><Pencil size={13} /></span>
                <span onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={13} /></span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><CalendarX size={15} /> Calendari / disponibilitat</h3>
          {selectedResource && <button onClick={() => setSelectedResource(null)}><X size={14} className="text-muted-foreground" /></button>}
        </div>
        {!selectedResource ? (
          <div className="p-6 text-center text-xs text-muted-foreground">Selecciona un recurs per gestionar festius, vacances, baixes o manteniments</div>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-900">{selectedResource.name}</p>
            <div className="grid grid-cols-2 gap-2">
              <select value={excForm.type} onChange={(e) => setExcForm((f) => ({ ...f, type: e.target.value as PlanningCalendarExceptionType }))} className="col-span-2 text-xs border border-gray-200 rounded px-2 py-1.5">
                {Object.entries(CALENDAR_EXCEPTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input type="date" value={excForm.startDate ?? ''} onChange={(e) => setExcForm((f) => ({ ...f, startDate: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
              <input type="date" value={excForm.endDate ?? ''} onChange={(e) => setExcForm((f) => ({ ...f, endDate: e.target.value }))} className="text-xs border border-gray-200 rounded px-2 py-1.5" />
              <input placeholder="Nota (opcional)" value={excForm.note ?? ''} onChange={(e) => setExcForm((f) => ({ ...f, note: e.target.value }))} className="col-span-2 text-xs border border-gray-200 rounded px-2 py-1.5" />
              <button onClick={addException} className="col-span-2 flex items-center justify-center gap-1 text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"><Plus size={13} /> Afegir</button>
            </div>
            <div className="divide-y divide-gray-100 border-t border-gray-100 pt-2">
              {exceptions.length === 0 ? (
                <p className="text-[11px] text-muted-foreground py-2">Sense excepcions registrades</p>
              ) : exceptions.map((e) => (
                <div key={e.id} className="flex items-center justify-between py-1.5 text-xs">
                  <div>
                    <span className="font-medium">{CALENDAR_EXCEPTION_LABEL[e.type]}</span>
                    <span className="text-muted-foreground"> · {e.startDate} a {e.endDate}</span>
                    {e.note && <span className="text-muted-foreground"> · {e.note}</span>}
                  </div>
                  <button onClick={() => removeException(e.id)} className="p-1 hover:bg-red-100 rounded text-red-600"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
